import { noop } from "@/libs/lodash"
import { clamp } from "@/libs/math"
import { InferenceSession, Tensor, env, getOptimizedSessionOptions } from "@/libs/onnx"
import { createAggregatedProgress } from "@/libs/progress"
import { interceptFetchRequest } from "@/libs/request/fetch-interceptor"
import { createResponseWithProgress, type Progress } from "@/libs/request/fetch-progress"
import { type FramePixels } from "../types.d"

export type Input = { frame: FramePixels; roi: number[] }
export type Output = {
  /** Light intensity in [0, 1] range */
  intensity: number
  /** Light horizontal angle deviation in [-1,1] range */
  azimuth: number
  /** Light vertical angle deviation in [-1, 1] range */
  elevation: number
}

type Dependency = { url: string; name: string }
export type Dependencies = readonly [wasm: Dependency, model: Dependency]
export type Model = Awaited<ReturnType<typeof create>>

export const create = async (
  [wasm, model]: Dependencies,
  options: { onProgress?: (progress: number) => void } = {},
) => {
  env.wasm.wasmPaths = { [wasm.name]: wasm.url }
  // Since we want to have a compatibility with the IOS < 16.4
  env.wasm.numThreads = 1

  const assets = [model.url, wasm.url]
  const onProgress = options.onProgress
    ? createAggregatedProgress({
        coeffs: Array.from({ length: assets.length }, () => 1 / assets.length),
        onProgress: options.onProgress,
        transform: (p: Progress) => p.complete,
      })
    : undefined
  // ONNXRuntime uses `fetch` API to load the model and wasm files
  // hook into it, to track the loading progress
  const interceptor = interceptFetchRequest((request, fetch) => {
    const idx = assets.findIndex((asset) => request.url.endsWith(asset))
    if (idx === -1) return

    return fetch(request).then((response) =>
      createResponseWithProgress(response, onProgress?.bind(null, idx) ?? noop),
    )
  })

  try {
    const session = await InferenceSession.create(
      model.url,
      getOptimizedSessionOptions({ executionProviders: ["wasm"] }),
    )

    return {
      predict: (input: Input) => predict(session, input),
      destroy: () => session.release(),
    }
  } finally {
    interceptor.terminate()
  }
}

/*
 * Ported from https://gitlab.com/haut.ai-team/sasuke/ml_algorithms/modelling/selfie/quality_face_illumination/-/blob/DS-1797_to_onnx_and_to_float16/source/onnx/inference.py?ref_type=heads
 *
 * Weight are located in Azure storage:
 * - Bucket: "hautrnd"
 * - Container: "saas/modelling/selfie/quality_face_illumination/illumination_estimation_green_rail_4lh5ktj92q_epoch_490.onnx"
 */

const DEFAULT_SIZE = new BigInt64Array([128, 128, 4].map((x) => BigInt(x)))

/** @returns prediction containing intensity in [0, 1] range and azimuth and elevation in [-1, 1] range,  */
const predict = async (
  session: InferenceSession,
  { frame: { data, height, width, format }, roi }: Input,
): Promise<Output> => {
  const [image_input_name, roi_input_name, size_input_name] = session.inputNames
  const [label_name] = session.outputNames

  const [azimuth, _, intensity] = (
    await session.run({
      [image_input_name]: new Tensor("uint8", data, [height, width, format.length]),
      [roi_input_name]: new Tensor("float32", new Float32Array(roi)),
      [size_input_name]: new Tensor("int64", DEFAULT_SIZE),
    })
  )[label_name].data as unknown as [azimuth: number, elevation: number, intensity: number]

  return {
    elevation: 0, // disabled until the prediction is not stable enough
    azimuth: clamp(azimuth, { min: -1, max: 1 }),
    intensity: clamp(intensity / 255, { min: 0, max: 1 }), // normalize to [0, 1]
  }
}
